import "./SurgicalPricingModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { useServiceDOI } from "components/DOIHeaderSelect/useServiceDOI";
import type { OptionType } from "components/inputs";
import { MaskedInput, Select, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";
import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import {
  useGetMMCsByInputQuery,
  useGetSurgicalPricingCalculationByIdQuery,
} from "services/future-medical-api";
import type { CostProjectionDataType, MSADataType } from "types/DataTypes";
import { useConstants } from "utils/hooks";

import type { AnesthesiaFormFields } from "./AnesthesiaSection";
import AnesthesiaSection from "./AnesthesiaSection";
import type { SurgicalPricingCptCode } from "./CPTCodeSection";
import CPTCodeSection from "./CPTCodeSection";
import type { FacilityFeeFormFields } from "./FacilityFeeSection";
import FacilityFeeSection from "./FacilityFeeSection";
import type { MMCOption } from "./mmcOptions";
import { buildMMCOptions, resolveMMCSelection } from "./mmcOptions";
import type { SurgicalProcedureType } from "./stateMultipliers";
import {
  DEFAULT_STATE_MULTIPLIER,
  getDefaultStateMultiplier,
  MAX_STATE_MULTIPLIER,
} from "./stateMultipliers";

export type SurgicalPricingFormData = {
  surgery_name: string;
  state: string;
  zip_code: string;
  mmc: string;
  procedure_type: SurgicalProcedureType;
  anesthesia_code: string;
  anesthesia_description: string;
  base_unit: number | null;
  total_units: number | null;
  conversion_factor: number | null;
  cpt_codes: SurgicalPricingCptCode[];
  adj_apc_payment: boolean;
  facility_fee_apc_cpt: number | null;
  ms_drg_code: string;
  ms_drg_description: string;
  ms_drg_base_price: number | null;
  state_multiplier: number;
  organization: number;
};

type SurgicalPricingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: SurgicalPricingFormData) => Promise<void>;
  organization: number;
  surgicalPricingId?: number | null;
  service: MSADataType | CostProjectionDataType;
};

type ProcedureTypeOption = {
  label: string;
  value: SurgicalProcedureType;
};

const procedureTypeOptions: ProcedureTypeOption[] = [
  { label: "Outpatient", value: "OUTPATIENT" },
  { label: "Inpatient", value: "INPATIENT" },
];

const FormErrors = ({
  errors,
}: {
  errors: FieldErrors<SurgicalPricingFormData>;
}) => {
  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="error-summary">
      {Object.entries(errors).map(([field, error]) => (
        <div key={field} className="error-message">
          {error?.message || `${field} is invalid`}
        </div>
      ))}
    </div>
  );
};

const defaultValues: SurgicalPricingFormData = {
  surgery_name: "",
  state: "",
  zip_code: "",
  mmc: "",
  procedure_type: "OUTPATIENT",
  anesthesia_code: "",
  anesthesia_description: "",
  base_unit: null,
  total_units: null,
  conversion_factor: null,
  cpt_codes: [],
  adj_apc_payment: false,
  facility_fee_apc_cpt: null,
  ms_drg_code: "",
  ms_drg_description: "",
  ms_drg_base_price: null,
  state_multiplier: DEFAULT_STATE_MULTIPLIER,
  organization: 0,
};

const SurgicalPricingModal = ({
  isOpen,
  onClose,
  onSubmit,
  organization,
  surgicalPricingId,
  service,
}: SurgicalPricingModalProps) => {
  const { constants } = useConstants();
  const { selectedDOI } = useServiceDOI(service);
  const [mmcSearchTerm, setMMCSearchTerm] = useState<string | null>(null);
  const [cptCodes, setCptCodes] = useState<SurgicalPricingCptCode[]>([]);

  const { data: existingSurgicalPricing, isLoading } =
    useGetSurgicalPricingCalculationByIdQuery(
      { id: surgicalPricingId },
      { skip: !surgicalPricingId },
    );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    watch,
  } = useForm<SurgicalPricingFormData>({
    defaultValues: {
      ...defaultValues,
      organization,
    },
    mode: "onBlur",
  });

  // only reset to defaults when explicitly creating a new calculation
  useEffect(() => {
    if (isOpen && !surgicalPricingId) {
      const initialState = selectedDOI?.jurisdiction || "";
      reset({
        ...defaultValues,
        organization,
        state: initialState,
        zip_code: selectedDOI?.wcmsa_zip_code || "",
        state_multiplier: getDefaultStateMultiplier(
          initialState,
          defaultValues.procedure_type,
        ),
      });
      setCptCodes([]);
    } else if (isOpen && existingSurgicalPricing) {
      reset(existingSurgicalPricing);
      setCptCodes(
        (existingSurgicalPricing as { cpt_codes: SurgicalPricingCptCode[] })
          .cpt_codes || [],
      );
    }
  }, [
    isOpen,
    surgicalPricingId,
    existingSurgicalPricing,
    reset,
    organization,
    selectedDOI,
  ]);

  // update state and zip code when DOI changes
  useEffect(() => {
    if (selectedDOI && isOpen) {
      const nextState = selectedDOI.jurisdiction || "";
      setValue("state", nextState, { shouldDirty: true });
      setValue("zip_code", selectedDOI.wcmsa_zip_code || "", {
        shouldDirty: true,
      });
      // a saved calculation keeps whatever multiplier was stored on it
      if (!surgicalPricingId) {
        setValue(
          "state_multiplier",
          getDefaultStateMultiplier(nextState, getValues("procedure_type")),
          { shouldDirty: true },
        );
      }
    }
  }, [selectedDOI, setValue, getValues, isOpen, surgicalPricingId]);

  const state = watch("state");
  const zipCode = watch("zip_code");
  const procedureType = watch("procedure_type");

  // the state multiplier defaults from the selected state + procedure type;
  // the user can still override it in the input below. A saved calculation
  // keeps whatever multiplier was stored on it (same rule as the DOI effect).
  const applyDefaultStateMultiplier = (
    nextState: string,
    nextProcedureType: SurgicalProcedureType,
  ) => {
    if (surgicalPricingId) return;
    setValue(
      "state_multiplier",
      getDefaultStateMultiplier(nextState, nextProcedureType),
      { shouldDirty: true },
    );
  };

  // initialize MMC search when modal opens
  useEffect(() => {
    if (isOpen && zipCode) {
      setMMCSearchTerm("");
    }
  }, [isOpen, zipCode]);

  const debouncedSetMMCSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setMMCSearchTerm(value);
      }, 500),
    [],
  );

  const { data: mmcResults, isFetching: isMMCFetching } =
    useGetMMCsByInputQuery(
      {
        search: mmcSearchTerm || undefined,
        zipcode: zipCode?.replace(/-/g, ""),
      },
      {
        skip: !zipCode,
      },
    );

  const mmcOptions = useMemo(
    () => buildMMCOptions(mmcResults?.results),
    [mmcResults],
  );

  // whenever the options change, auto-select the first MMC unless the current
  // value (a saved MMC or an explicit "N/A") is still one of the options.
  // `currentMMC` is a dependency on purpose: after a `setValue` the effect
  // runs once more, finds the value already resolved, and stops (no loop).
  const currentMMC = watch("mmc");
  useEffect(() => {
    if (!isOpen) return;
    const nextMMC = resolveMMCSelection(mmcOptions, currentMMC);
    if (nextMMC !== currentMMC) {
      setValue("mmc", nextMMC);
    }
  }, [mmcOptions, currentMMC, setValue, isOpen]);

  const handleAddCptCode = () => {
    const updatedCptCodes = [
      ...cptCodes,
      { code: "", description: "", price: 0 },
    ];
    setCptCodes(updatedCptCodes);
    setValue("cpt_codes", updatedCptCodes);
  };

  const handleRemoveCptCode = (index: number) => {
    const updatedCptCodes = [...cptCodes];
    updatedCptCodes.splice(index, 1);
    setCptCodes(updatedCptCodes);
    setValue("cpt_codes", updatedCptCodes);
  };

  const handleCptCodeChange = (
    index: number,
    updates: Partial<SurgicalPricingCptCode>,
  ) => {
    const updatedCptCodes = cptCodes.map((code, i) =>
      i === index
        ? {
            ...code,
            ...updates,
          }
        : code,
    );
    setCptCodes(updatedCptCodes);
    setValue("cpt_codes", updatedCptCodes);
  };

  const handleFormSubmit = async () => {
    if (onSubmit) {
      handleSubmit(onSubmit)();
    }
  };

  if (isLoading) {
    return (
      <KaboodleModal isOpen={isOpen} onClose={onClose}>
        <Card heading="Loading...">
          <div className="loading-spinner" />
        </Card>
      </KaboodleModal>
    );
  }

  return (
    <KaboodleModal
      size="large"
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex ai-c jc-c">
          <Button onClick={handleFormSubmit}>
            {surgicalPricingId ? "Update" : "Save + Add"}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Surgical Pricing Calculations">
        <FormErrors errors={errors} />
        <Controller
          name="surgery_name"
          control={control}
          rules={{
            required: "Surgery name is required",
            minLength: {
              value: 3,
              message: "Surgery name must be at least 3 characters",
            },
          }}
          render={({ field }) => (
            <TextInput {...field} label="Surgery Name" errors={errors} />
          )}
        />

        <Controller
          name="procedure_type"
          control={control}
          rules={{ required: "Procedure type is required" }}
          render={({ field: { onChange, value, ...field } }) => (
            <Select
              {...field}
              options={procedureTypeOptions}
              value={
                procedureTypeOptions.find((option) => option.value === value) ||
                procedureTypeOptions[0]
              }
              errors={errors}
              label="Procedure Type"
              onChange={(option) => {
                const nextProcedureType = option
                  ? (option as ProcedureTypeOption).value
                  : "OUTPATIENT";
                onChange(nextProcedureType);
                applyDefaultStateMultiplier(state, nextProcedureType);
              }}
            />
          )}
        />

        <Controller
          name="state"
          control={control}
          rules={{ required: "State is required" }}
          render={({ field: { onChange, value, ...field } }) => (
            <Select
              {...field}
              options={constants?.STATES || []}
              value={
                constants?.STATES?.find((option) => option.value === value) ||
                null
              }
              errors={errors}
              label="State"
              onChange={(option: OptionType) => {
                const nextState = option ? String(option.value ?? "") : "";
                onChange(nextState);
                applyDefaultStateMultiplier(nextState, procedureType);
              }}
            />
          )}
        />

        <Controller
          name="zip_code"
          control={control}
          rules={{
            required: "Zip code is required",
          }}
          render={({ field }) => (
            <MaskedInput
              {...field}
              mask="zipcode-nine"
              label="Zip Code"
              errors={errors}
            />
          )}
        />

        <Controller
          name="mmc"
          control={control}
          rules={{
            required: "MMC is required",
          }}
          render={({ field: { onChange, value, ...field } }) => (
            <Select
              {...field}
              options={mmcOptions}
              value={
                value
                  ? (mmcOptions.find((option) => option.value === value) ?? {
                      label: value,
                      value,
                    })
                  : null
              }
              errors={errors}
              label="MMC"
              placeholder="Select or search MMCs..."
              noOptionsMessage={() =>
                !state || !zipCode
                  ? "Enter state and zip code first..."
                  : isMMCFetching
                    ? "Loading..."
                    : "No MMCs found for this location"
              }
              onInputChange={(value: string) =>
                debouncedSetMMCSearchTerm(value)
              }
              onChange={(option: MMCOption | null) => {
                onChange(option ? option.value : "");
              }}
            />
          )}
        />

        <div className="border mrg-bottom"></div>

        <CPTCodeSection
          cptCodes={cptCodes}
          errors={errors}
          onAdd={handleAddCptCode}
          onRemove={handleRemoveCptCode}
          onChange={handleCptCodeChange}
        />

        <div className="border mrg-bottom"></div>

        <AnesthesiaSection
          control={control as unknown as Control<AnesthesiaFormFields>}
          errors={errors as unknown as FieldErrors<AnesthesiaFormFields>}
          setValue={
            setValue as unknown as UseFormSetValue<AnesthesiaFormFields>
          }
        />

        <div className="border mrg-bottom"></div>

        <FacilityFeeSection
          control={control as unknown as Control<FacilityFeeFormFields>}
          errors={errors as unknown as FieldErrors<FacilityFeeFormFields>}
          cptCodes={cptCodes}
          setValue={
            setValue as unknown as UseFormSetValue<FacilityFeeFormFields>
          }
          watch={watch as unknown as UseFormWatch<FacilityFeeFormFields>}
        />

        <div className="border mrg-bottom"></div>

        <div className="header-primary grid-2-wide">Total Surgical Fee</div>

        <Controller
          name="state_multiplier"
          control={control}
          rules={{
            validate: (value) => {
              if (isNaN(value) || value <= 0) {
                return "State multiplier must be a positive number";
              }
              // DecimalField(max_digits=4, decimal_places=2) on the backend
              if (value > MAX_STATE_MULTIPLIER) {
                return `State multiplier must be at most ${MAX_STATE_MULTIPLIER}`;
              }
              return true;
            },
          }}
          render={({ field: { value, onChange, ...field } }) => (
            <TextInput
              {...field}
              value={value ?? ""}
              onChange={(e) =>
                onChange(
                  e.target.value
                    ? Number(e.target.value)
                    : getDefaultStateMultiplier(state, procedureType),
                )
              }
              type="number"
              step="0.01"
              label="State Multiplier"
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default SurgicalPricingModal;
